/**
 * One space: its child spaces, its node tree, and the grants written on it.
 *
 * The node list arrives already filtered by the set-based resolution query
 * (`permission_visible_nodes`), not by this app calling `can` per row. §4.9's
 * property test is what keeps that query and the per-node function agreeing, so
 * the tree shown here and the answers in the effective-access views come from
 * two implementations that are pinned to each other by a test rather than by
 * hope.
 */

import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { Form } from 'react-aria-components';
import { useCreateNode, useDeleteNode, useSpace, useSpaceNodes } from '../../api/queries.ts';
import { NODE_TYPES, type NodeType, type TreeNode } from '../../api/types.ts';
import { CAPABILITY_OPTIONS } from '../../components/capabilities.ts';
import { GrantsPanel } from '../../components/grants-panel.tsx';
import { SpaceTree } from '../../components/tree/space-tree.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import {
  Alert,
  EmptyState,
  ErrorState,
  Loading,
  PageError,
} from '../../components/ui/feedback.tsx';
import { Select, TextField } from '../../components/ui/field.tsx';
import { Card, Definition, DefinitionList, Mono, PageHeader } from '../../components/ui/layout.tsx';

function CreateNodeForm({
  spaceId,
  nodes,
  onClose,
}: {
  spaceId: string;
  nodes: TreeNode[];
  onClose: () => void;
}) {
  const create = useCreateNode();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [type, setType] = useState<NodeType>('document');
  const [parentNodeId, setParentNodeId] = useState('');

  const parentOptions = [
    { value: '', label: 'None — top level in this space' },
    ...nodes.map((node) => ({ value: node.id, label: node.title })),
  ];

  /**
   * A document is not created here.
   *
   * `POST /api/nodes` writes a `node` row and nothing else, which was the whole
   * of a node in Phase 0. Phase 1a hung a payload table off it: a `document`
   * node with no `document` row has no body, no owner-facing review date, and
   * no version history, and `GET /api/documents/:id` answers 404 for it — so
   * the tree would link somewhere broken. `POST /api/documents` is the route
   * that writes both, and `/documents/new` is the form for it.
   */
  const isDocument = type === 'document';

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        if (isDocument) {
          void navigate({
            to: '/documents/new',
            search: { spaceId, ...(parentNodeId ? { parentNodeId } : {}) },
          });
          onClose();
          return;
        }
        create.mutate(
          { spaceId, title: title.trim(), type, ...(parentNodeId ? { parentNodeId } : {}) },
          { onSuccess: onClose },
        );
      }}
    >
      <DialogBody>
        <TextField
          label="Title"
          value={title}
          onChange={setTitle}
          isRequired={!isDocument}
          isDisabled={isDocument}
          autoFocus
        />
        <Select
          label="Type"
          options={NODE_TYPES.map((value) => ({ value, label: value }))}
          selectedKey={type}
          onSelectionChange={setType}
          description="A node is a labeled position in the tree — enough to grant against. A file or a link gets its payload when the bytes or the URL arrive."
        />
        <Select
          label="Parent node"
          options={parentOptions}
          selectedKey={parentNodeId}
          onSelectionChange={setParentNodeId}
          description="A child node must be in the same space as its parent, which the schema enforces with a composite foreign key."
        />
        {isDocument ? (
          <Alert tone="info" title="Documents are created on their own form">
            A document is a node <em>and</em> a body, written together in one transaction. Continue
            to pick its type — playbook, process, meeting note — and give it an owner and a review
            date, with this space and parent already filled in.
          </Alert>
        ) : null}
        {create.isError ? <ErrorState error={create.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isPending={create.isPending}
          isDisabled={!isDocument && title.trim() === ''}
        >
          {isDocument ? 'Continue to the document form' : 'Create node'}
        </Button>
      </DialogFooter>
    </Form>
  );
}

export function SpaceDetailPage() {
  const { spaceId } = useParams({ from: '/_authed/_employee/spaces/$spaceId' });
  const [capability, setCapability] = useState('document.view');
  const space = useSpace(spaceId);
  const nodes = useSpaceNodes(spaceId, capability);
  const deleteNode = useDeleteNode(spaceId);
  const [creating, setCreating] = useState(false);

  if (space.isPending) return <Loading />;
  if (space.isError) {
    return (
      <PageError
        error={space.error}
        title="Space"
        back={{ href: '/schema/spaces', label: 'Back to spaces' }}
      />
    );
  }

  const { space: current, children, organization } = space.data;

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link to="/schema/spaces" className="text-tertiary no-underline">
            Spaces
          </Link>
        }
        title={current.name}
        actions={
          <Button variant="primary" onPress={() => setCreating(true)}>
            Create node
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        {/*
         * `components/tree/` rather than a copy of it. This page and the search
         * results are the two places a node is drawn, and when they were two
         * implementations only one of them learned §4.6's external marker and
         * §7.2's review marker. The subtle part — a node whose parent this
         * person cannot reach surfaces at the top rather than vanishing — is in
         * `build.ts` now, tested, in one place.
         */}
        <SpaceTree
          spaceId={spaceId}
          capability={capability}
          description="Filtered to what you can reach, by the set-based query rather than a per-row check."
          headerActions={
            <Select
              label="Measured against"
              className="w-60"
              options={CAPABILITY_OPTIONS}
              selectedKey={capability}
              onSelectionChange={setCapability}
            />
          }
          emptyState={
            <EmptyState
              title="No nodes you can reach here"
              description={`Either this space has none, or nothing grants you ${capability} on any of them.`}
            />
          }
          actions={(node) => (
            <>
              <Link
                to="/schema/access/nodes/$nodeId"
                params={{ nodeId: node.id }}
                className="text-xs link"
              >
                Who can reach this
              </Link>
              <Button
                size="sm"
                variant="tertiary"
                className="text-error-primary"
                onPress={() => deleteNode.mutate(node.id)}
              >
                Delete
              </Button>
            </>
          )}
          footer={
            <>
              {deleteNode.isSuccess ? (
                <p className="border-t border-secondary px-4 py-3 text-sm text-tertiary">
                  Soft-deleted {deleteNode.data.deleted} node(s) — the node and its whole subtree,
                  in one transaction.
                </p>
              ) : null}
              {deleteNode.isError ? (
                <div className="p-5">
                  <ErrorState error={deleteNode.error} />
                </div>
              ) : null}
            </>
          }
        />

        <div className="flex flex-col gap-6">
          <Card>
            <h2 className="mb-4 text-md font-semibold text-primary">Space</h2>
            <DefinitionList>
              <Definition term="Kind">
                <Badge tone={current.kind === 'client' ? 'brand' : 'outline'}>{current.kind}</Badge>
                {current.kind === 'client' ? (
                  <span className="ml-2 text-xs text-tertiary">Cannot have children.</span>
                ) : null}
              </Definition>
              <Definition term="Parent">
                {current.parentSpaceId ? (
                  <Link to="/spaces/$spaceId" params={{ spaceId: current.parentSpaceId }}>
                    View parent
                  </Link>
                ) : (
                  <span className="text-tertiary">Root of the tree</span>
                )}
              </Definition>
              <Definition term="Organization">
                {organization ? (
                  <Link
                    to="/organizations/$organizationId"
                    params={{ organizationId: organization.id }}
                  >
                    {organization.name}
                  </Link>
                ) : (
                  <span className="text-tertiary">None — an internal space</span>
                )}
              </Definition>
              <Definition term="Identifier">
                <Mono>{current.id}</Mono>
              </Definition>
            </DefinitionList>
          </Card>

          {children.length > 0 ? (
            <Card>
              <h2 className="mb-3 text-md font-semibold text-primary">Child spaces</h2>
              <ul className="flex flex-col gap-2">
                {children.map((child) => (
                  <li key={child.id} className="flex items-center gap-2 text-sm">
                    <Link to="/spaces/$spaceId" params={{ spaceId: child.id }}>
                      {child.name}
                    </Link>
                    <Badge tone="outline">{child.kind}</Badge>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </div>
      </div>

      <GrantsPanel subjectType="space" subjectId={current.id} subjectLabel={current.name} />

      <Dialog title="Create a node" isOpen={creating} onOpenChange={setCreating}>
        {(close) => (
          <CreateNodeForm spaceId={spaceId} nodes={nodes.data?.nodes ?? []} onClose={close} />
        )}
      </Dialog>
    </>
  );
}
